import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCcw } from 'lucide-react';
import {
  callGetBillingDashboard,
  callUpdateAiRuntimeConfig,
  type AiRuntimeConfig,
  type BillingDashboardResponse,
  type BillingDailyPoint,
  type BillingGroupRow,
  type BillingRecentEvent,
  type BillingStackDimension,
  type BillingStackedSeries,
  type SupportedRouteKind,
  type TextModelPriceEntry,
} from '../services/functions';

const DAY_OPTIONS = [1, 3, 7, 30, 90, 180, 365] as const;
const STACK_OPTIONS: Array<{ id: BillingStackDimension; label: string }> = [
  { id: 'provider', label: 'Provider' },
  { id: 'service', label: 'Service' },
  { id: 'model', label: 'Model' },
  { id: 'user', label: 'User' },
  { id: 'channel', label: 'Channel' },
  { id: 'eventType', label: 'Event type' },
];
const STACK_COLORS = [
  '#5BB2C3',
  '#457AC9',
  '#F59E0B',
  '#D946EF',
  '#22C55E',
  '#EF4444',
  '#A855F7',
  '#14B8A6',
  '#F97316',
  '#6B7280',
];

function formatUsdMicros(micros: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(micros / 1_000_000);
}

function formatDate(valueMs: number): string {
  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(valueMs));
}

function formatProvider(provider: string): string {
  if (provider === 'openai') return 'OpenAI';
  if (provider === 'google') return 'Google';
  if (provider === 'anthropic') return 'Anthropic';
  return capitalizeWords(provider);
}

function formatMinutes(value: number): string {
  return `${value.toFixed(1)} min`;
}

function formatDayOptionLabel(days: number): string {
  return days === 1 ? '1 day' : `${days} days`;
}

function compactDate(dateKey: string): string {
  return dateKey.slice(5);
}

function capitalizeWords(text: string): string {
  return text
    .split(/[_\s.]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function BreakdownCard({
  title,
  rows,
  emptyLabel,
}: {
  title: string;
  rows: BillingGroupRow[];
  emptyLabel: string;
}) {
  return (
    <section className="flank-card rounded-2xl p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)] mt-3">{emptyLabel}</p>
      ) : (
        <div className="mt-3 flex flex-col gap-2.5">
          {rows.map(row => (
            <div key={row.key} className="rounded-xl border p-2.5" style={{ borderColor: 'var(--color-border)' }}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-medium text-[var(--color-text-primary)] truncate">{capitalizeWords(row.label)}</span>
                <span className="text-xs text-[var(--color-text-secondary)]">{formatUsdMicros(row.costMicros)}</span>
              </div>
              <div className="mt-1.5 h-1.5 rounded-full bg-[var(--color-surface)] overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.max(4, Math.min(100, Math.round(row.share * 100)))}%`,
                    background: 'var(--color-brand-primary)',
                  }}
                />
              </div>
              <div className="mt-1 flex items-center justify-between text-[11px] text-[var(--color-text-muted)]">
                <span>{row.eventCount} events</span>
                <span>{(row.share * 100).toFixed(1)}%</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function DailyTrend({ points }: { points: BillingDailyPoint[] }) {
  const maxCost = useMemo(() => {
    if (points.length === 0) return 0;
    return Math.max(...points.map(point => point.costMicros), 0);
  }, [points]);

  return (
    <section className="flank-card rounded-2xl p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Daily Spend</h3>
      {points.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)] mt-3">No daily points in this range yet.</p>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          {points.slice(-14).map(point => {
            const barWidth = maxCost > 0 ? Math.max(3, Math.round((point.costMicros / maxCost) * 100)) : 0;
            return (
              <div key={point.date}>
                <div className="flex items-center justify-between text-xs text-[var(--color-text-secondary)]">
                  <span>{point.date}</span>
                  <span>{formatUsdMicros(point.costMicros)}</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-[var(--color-surface)] overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${barWidth}%`, background: 'var(--color-brand-primary)' }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function StackedDailyChart({
  series,
  selectedDimension,
  onChangeDimension,
}: {
  series: BillingStackedSeries[];
  selectedDimension: BillingStackDimension;
  onChangeDimension: (dimension: BillingStackDimension) => void;
}) {
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);

  const activeSeries = useMemo(
    () => series.find(item => item.dimension === selectedDimension) ?? series[0],
    [series, selectedDimension]
  );

  useEffect(() => {
    setHighlightedKey(null);
  }, [selectedDimension]);

  const colorMap = useMemo(() => {
    const map = new Map<string, string>();
    activeSeries?.categories.forEach((category, index) => {
      map.set(category.key, STACK_COLORS[index % STACK_COLORS.length]);
    });
    return map;
  }, [activeSeries]);

  const maxTotal = useMemo(() => {
    if (!activeSeries || activeSeries.days.length === 0) return 0;
    return Math.max(...activeSeries.days.map(day => day.totalCostMicros), 0);
  }, [activeSeries]);

  const toggleHighlight = (key: string) => {
    setHighlightedKey(prev => (prev === key ? null : key));
  };

  return (
    <section className="flank-card rounded-2xl p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Stacked Daily Spend</h3>
        <label className="text-xs text-[var(--color-text-secondary)]">
          Stack by
          <select
            value={selectedDimension}
            onChange={event => onChangeDimension(event.target.value as BillingStackDimension)}
            className="ml-2 rounded-lg border px-2 py-1 text-xs bg-[var(--color-background)] text-[var(--color-text-primary)]"
            style={{ borderColor: 'var(--color-border)' }}
          >
            {STACK_OPTIONS.map(option => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {!activeSeries || activeSeries.days.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)] mt-3">No daily stacked data in this range.</p>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto">
            <div className="min-w-[860px]">
              <div className="flex items-end gap-2 h-56 px-1">
                {activeSeries.days.map(day => (
                  <div key={day.date} className="flex flex-col items-center gap-1 w-6">
                    <div className="h-44 w-full rounded-md bg-[var(--color-surface)] border border-[var(--color-border)] flex flex-col-reverse overflow-hidden">
                      {day.segments.map(segment => {
                        const heightPct = maxTotal > 0 ? (segment.costMicros / maxTotal) * 100 : 0;
                        const dimmed = highlightedKey !== null && segment.key !== highlightedKey;
                        return (
                          <div
                            key={`${day.date}-${segment.key}`}
                            title={`${segment.label}: ${formatUsdMicros(segment.costMicros)} on ${day.date}`}
                            style={{
                              height: `${Math.max(0.8, heightPct)}%`,
                              background: colorMap.get(segment.key) ?? 'var(--color-brand-primary)',
                              opacity: dimmed ? 0.2 : 1,
                              transition: 'opacity 200ms ease',
                            }}
                          />
                        );
                      })}
                    </div>
                    <span className="text-[10px] text-[var(--color-text-muted)]">{compactDate(day.date)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="mt-2 text-[11px] text-[var(--color-text-muted)]">
            Y-axis = daily cost. X-axis = day. Max day: {formatUsdMicros(maxTotal)}.
          </p>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {activeSeries.categories.map(category => {
              const isActive = highlightedKey === null || highlightedKey === category.key;
              return (
                <button
                  type="button"
                  key={category.key}
                  onClick={() => toggleHighlight(category.key)}
                  className="rounded-lg border px-2.5 py-2 flex items-center justify-between gap-2 text-left"
                  style={{
                    borderColor: highlightedKey === category.key
                      ? colorMap.get(category.key) ?? 'var(--color-border)'
                      : 'var(--color-border)',
                    opacity: isActive ? 1 : 0.4,
                    transition: 'opacity 200ms ease, border-color 200ms ease',
                    cursor: 'pointer',
                  }}
                >
                  <span className="inline-flex items-center gap-2 min-w-0">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ background: colorMap.get(category.key) ?? 'var(--color-brand-primary)' }}
                    />
                    <span className="text-xs text-[var(--color-text-primary)] truncate">{capitalizeWords(category.label)}</span>
                  </span>
                  <span className="text-xs text-[var(--color-text-secondary)]">{formatUsdMicros(category.costMicros)}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}

function VoiceSpendSection({
  data,
}: {
  data: BillingDashboardResponse['voice'];
}) {
  return (
    <section className="flank-card rounded-2xl p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Voice Spend Details</h3>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-2.5">
        <MetricCard label="Voice Total" value={formatUsdMicros(data.totalCostMicros)} />
        <MetricCard label="Voice Minutes" value={formatMinutes(data.totalMinutes)} />
        <MetricCard label="Phone Voice" value={formatUsdMicros(data.phoneCostMicros)} />
        <MetricCard label="Web Voice" value={formatUsdMicros(data.webCostMicros)} />
        <MetricCard label="STT" value={formatUsdMicros(data.sttCostMicros)} />
        <MetricCard label="TTS" value={formatUsdMicros(data.ttsCostMicros)} />
      </div>

      <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
        <BreakdownCard
          title="Voice Spend by Provider"
          rows={data.byProvider}
          emptyLabel="No voice provider spend found."
        />
        <BreakdownCard
          title="Voice Spend by Service"
          rows={data.byService}
          emptyLabel="No voice service spend found."
        />
      </div>
    </section>
  );
}

function RoutingControlsSection({
  config,
  onChange,
  onSave,
  saving,
}: {
  config: AiRuntimeConfig;
  onChange: (next: AiRuntimeConfig) => void;
  onSave: () => Promise<void>;
  saving: boolean;
}) {
  const selectableModels = useMemo(
    () => config.pricingCatalog.models.filter((entry) => entry.supportedRoutes.includes('postSession') || entry.supportedRoutes.includes('realtime')),
    [config]
  );

  const routeOptionsFor = useCallback((kind: SupportedRouteKind) => {
    return selectableModels.filter((entry) => entry.supportedRoutes.includes(kind));
  }, [selectableModels]);

  const updateRoute = (kind: 'postSession' | 'realtime', value: string) => {
    const entry = selectableModels.find((item) => `${item.provider}:${item.model}` === value);
    if (!entry) return;
    onChange({
      ...config,
      extraction: {
        ...config.extraction,
        [kind]: {
          provider: entry.provider,
          model: entry.model,
          label: entry.label,
        },
      },
    });
  };

  const updatePrice = (
    target: TextModelPriceEntry,
    key: 'inputUsdPerMillion' | 'outputUsdPerMillion',
    rawValue: string
  ) => {
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed) || parsed < 0) return;
    onChange({
      ...config,
      pricingCatalog: {
        ...config.pricingCatalog,
        models: config.pricingCatalog.models.map((entry) => {
          if (entry.provider !== target.provider || entry.model !== target.model) return entry;
          return {
            ...entry,
            [key]: parsed,
          };
        }),
      },
    });
  };

  const routeValue = (route: AiRuntimeConfig['extraction']['postSession']) => `${route.provider}:${route.model}`;

  return (
    <section className="flank-card rounded-2xl p-4 sm:p-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">AI Runtime Routing</h3>
          <p className="text-xs text-[var(--color-text-secondary)] mt-1">
            Coaching stays on {config.coaching.textModel.label}. Extraction routes and prices below are Firebase-backed.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void onSave()}
          className="flank-btn flank-btn-primary"
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Routing'}
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="rounded-xl border p-3" style={{ borderColor: 'var(--color-border)' }}>
          <p className="text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">Coaching</p>
          <p className="text-sm font-medium text-[var(--color-text-primary)] mt-1">{config.coaching.textModel.label}</p>
          <p className="text-xs text-[var(--color-text-secondary)] mt-1">
            {formatProvider(config.coaching.textModel.provider)} · `{config.coaching.textModel.model}`
          </p>
        </div>

        <label className="rounded-xl border p-3 block" style={{ borderColor: 'var(--color-border)' }}>
          <span className="text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">Post-session extraction</span>
          <select
            value={routeValue(config.extraction.postSession)}
            onChange={(event) => updateRoute('postSession', event.target.value)}
            className="mt-2 w-full rounded-lg border px-3 py-2 text-sm bg-[var(--color-background)] text-[var(--color-text-primary)]"
            style={{ borderColor: 'var(--color-border)' }}
          >
            {routeOptionsFor('postSession').map((entry) => (
              <option key={`${entry.provider}:${entry.model}`} value={`${entry.provider}:${entry.model}`}>
                {entry.label} ({formatProvider(entry.provider)})
              </option>
            ))}
          </select>
          <p className="text-xs text-[var(--color-text-secondary)] mt-2">
            Current: `{config.extraction.postSession.model}`
          </p>
        </label>

        <label className="rounded-xl border p-3 block" style={{ borderColor: 'var(--color-border)' }}>
          <span className="text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">Realtime extraction</span>
          <select
            value={routeValue(config.extraction.realtime)}
            onChange={(event) => updateRoute('realtime', event.target.value)}
            className="mt-2 w-full rounded-lg border px-3 py-2 text-sm bg-[var(--color-background)] text-[var(--color-text-primary)]"
            style={{ borderColor: 'var(--color-border)' }}
          >
            {routeOptionsFor('realtime').map((entry) => (
              <option key={`${entry.provider}:${entry.model}`} value={`${entry.provider}:${entry.model}`}>
                {entry.label} ({formatProvider(entry.provider)})
              </option>
            ))}
          </select>
          <p className="text-xs text-[var(--color-text-secondary)] mt-2">
            Current: `{config.extraction.realtime.model}`
          </p>
        </label>
      </div>

      <div className="mt-4 rounded-xl border p-3" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-medium text-[var(--color-text-primary)]">Price Catalog</p>
            <p className="text-xs text-[var(--color-text-secondary)] mt-1">
              Version `{config.pricingCatalog.version}` effective {formatDate(config.pricingCatalog.effectiveAtMs)}
            </p>
          </div>
          <p className="text-xs text-[var(--color-text-muted)]">
            {config.updatedAtMs ? `Last saved ${formatDate(config.updatedAtMs)}` : 'Using default catalog'}
          </p>
        </div>

        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-left text-[var(--color-text-muted)]">
                <th className="py-2 pr-3 font-medium">Model</th>
                <th className="py-2 pr-3 font-medium">Provider</th>
                <th className="py-2 pr-3 font-medium">Routes</th>
                <th className="py-2 pr-3 font-medium">Input / 1M</th>
                <th className="py-2 font-medium">Output / 1M</th>
              </tr>
            </thead>
            <tbody>
              {config.pricingCatalog.models.map((entry) => (
                <tr key={`${entry.provider}:${entry.model}`} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                  <td className="py-2 pr-3 text-[var(--color-text-primary)]">
                    <div>{entry.label}</div>
                    <div className="text-[11px] text-[var(--color-text-muted)] mt-0.5">`{entry.model}`</div>
                  </td>
                  <td className="py-2 pr-3 text-[var(--color-text-secondary)]">{formatProvider(entry.provider)}</td>
                  <td className="py-2 pr-3 text-[var(--color-text-secondary)]">{entry.supportedRoutes.join(', ')}</td>
                  <td className="py-2 pr-3">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={entry.inputUsdPerMillion}
                      onChange={(event) => updatePrice(entry, 'inputUsdPerMillion', event.target.value)}
                      className="w-28 rounded-lg border px-2 py-1 bg-[var(--color-background)] text-[var(--color-text-primary)]"
                      style={{ borderColor: 'var(--color-border)' }}
                    />
                  </td>
                  <td className="py-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={entry.outputUsdPerMillion}
                      onChange={(event) => updatePrice(entry, 'outputUsdPerMillion', event.target.value)}
                      className="w-28 rounded-lg border px-2 py-1 bg-[var(--color-background)] text-[var(--color-text-primary)]"
                      style={{ borderColor: 'var(--color-border)' }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function RecentEvents({ events }: { events: BillingRecentEvent[] }) {
  return (
    <section className="flank-card rounded-2xl p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Recent Billing Events</h3>
      {events.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)] mt-3">No events in this range.</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-left text-[var(--color-text-muted)]">
                <th className="py-2 pr-3 font-medium">Time</th>
                <th className="py-2 pr-3 font-medium">User</th>
                <th className="py-2 pr-3 font-medium">Service</th>
                <th className="py-2 pr-3 font-medium">Model</th>
                <th className="py-2 pr-3 font-medium">Type</th>
                <th className="py-2 font-medium text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {events.map(event => (
                <tr key={event.eventId} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                  <td className="py-2 pr-3 text-[var(--color-text-secondary)]">{formatDate(event.eventTimeMs)}</td>
                  <td className="py-2 pr-3 text-[var(--color-text-primary)]">{event.userLabel}</td>
                  <td className="py-2 pr-3 text-[var(--color-text-secondary)]">
                    {capitalizeWords(event.sourceFunction || event.sourceService)}
                  </td>
                  <td className="py-2 pr-3 text-[var(--color-text-secondary)]">{event.model || '—'}</td>
                  <td className="py-2 pr-3 text-[var(--color-text-secondary)]">
                    {capitalizeWords(event.eventType)} {event.amountStatus === 'estimated' ? '(est.)' : ''}
                  </td>
                  <td className="py-2 text-right text-[var(--color-text-primary)]">{formatUsdMicros(event.totalCostMicros)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function BillingDashboard() {
  const [days, setDays] = useState<number>(30);
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [stackDimension, setStackDimension] = useState<BillingStackDimension>('provider');
  const [data, setData] = useState<BillingDashboardResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [configDraft, setConfigDraft] = useState<AiRuntimeConfig | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configNotice, setConfigNotice] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await callGetBillingDashboard({
        days,
        userId: selectedUserId === 'all' ? null : selectedUserId,
      });
      setData(result);
      setConfigDraft(result.aiRuntimeConfig);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load billing dashboard');
    } finally {
      setLoading(false);
    }
  }, [days, selectedUserId]);

  useEffect(() => {
    void fetchDashboard();
  }, [fetchDashboard, refreshCounter]);

  useEffect(() => {
    if (!data) return;
    const available = new Set(data.stackedDaily.map(item => item.dimension));
    if (!available.has(stackDimension) && data.stackedDaily.length > 0) {
      setStackDimension(data.stackedDaily[0].dimension);
    }
  }, [data, stackDimension]);

  const subtitle = useMemo(() => {
    if (!data) return 'Loading spend data...';
    if (selectedUserId === 'all') {
      return `All users · Last ${data.range.days} days`;
    }
    const user = data.users.find(item => item.userId === selectedUserId);
    const userLabel = user?.displayName || selectedUserId;
    return `${userLabel} · Last ${data.range.days} days`;
  }, [data, selectedUserId]);

  const saveRuntimeConfig = useCallback(async () => {
    if (!configDraft) return;
    setSavingConfig(true);
    setConfigNotice(null);
    try {
      const nextConfig = await callUpdateAiRuntimeConfig({
        extraction: {
          postSession: {
            provider: configDraft.extraction.postSession.provider,
            model: configDraft.extraction.postSession.model,
          },
          realtime: {
            provider: configDraft.extraction.realtime.provider,
            model: configDraft.extraction.realtime.model,
          },
        },
        pricingCatalog: {
          models: configDraft.pricingCatalog.models.map((entry) => ({
            provider: entry.provider,
            model: entry.model,
            inputUsdPerMillion: entry.inputUsdPerMillion,
            outputUsdPerMillion: entry.outputUsdPerMillion,
          })),
        },
      });
      setConfigDraft(nextConfig);
      setConfigNotice('AI routing and pricing saved.');
      setRefreshCounter((value) => value + 1);
    } catch (err) {
      setConfigNotice(err instanceof Error ? err.message : 'Failed to save AI routing');
    } finally {
      setSavingConfig(false);
    }
  }, [configDraft]);

  return (
    <div className="w-full flex flex-col gap-4">
      <section className="flank-card rounded-2xl p-4 sm:p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Billing Dashboard</h2>
            <p className="text-xs text-[var(--color-text-secondary)] mt-1">{subtitle}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-[var(--color-text-secondary)]">
              Window
              <select
                value={days}
                onChange={event => setDays(Number(event.target.value))}
                className="ml-2 rounded-lg border px-2 py-1 text-xs bg-[var(--color-background)] text-[var(--color-text-primary)]"
                style={{ borderColor: 'var(--color-border)' }}
              >
                {DAY_OPTIONS.map(option => (
                  <option key={option} value={option}>
                    {formatDayOptionLabel(option)}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-[var(--color-text-secondary)]">
              User
              <select
                value={selectedUserId}
                onChange={event => setSelectedUserId(event.target.value)}
                className="ml-2 rounded-lg border px-2 py-1 text-xs bg-[var(--color-background)] text-[var(--color-text-primary)] max-w-[240px]"
                style={{ borderColor: 'var(--color-border)' }}
              >
                <option value="all">All users</option>
                {(data?.users ?? []).map(user => (
                  <option key={user.userId} value={user.userId}>
                    {user.displayName} ({formatUsdMicros(user.costMicros)})
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={() => setRefreshCounter(value => value + 1)}
              className="flank-btn flank-btn-secondary flex items-center gap-1.5"
              disabled={loading}
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
              Refresh
            </button>
          </div>
        </div>
        {data?.range.truncated && (
          <p className="text-[11px] text-[var(--color-warning)] mt-3">
            Showing first {data.range.scannedEventCount} events in range. Add rollups for larger history.
          </p>
        )}
        <p className="text-[11px] text-[var(--color-text-muted)] mt-3">
          Aggregates scan canonical `billingEvents` plus legacy `billing` rows, then dedupe by event ID.
        </p>
      </section>

      {error && (
        <section
          className="rounded-2xl border p-3 text-xs flex items-center gap-2"
          style={{
            borderColor: 'color-mix(in srgb, var(--color-error) 35%, var(--color-border))',
            color: 'var(--color-error)',
            background: 'color-mix(in srgb, var(--color-error) 8%, transparent)',
          }}
        >
          <AlertTriangle size={14} />
          {error}
        </section>
      )}

      {configNotice && (
        <section className="flank-card rounded-2xl p-3 text-xs text-[var(--color-text-secondary)]">
          {configNotice}
        </section>
      )}

      {!data || loading ? (
        <section className="flank-card rounded-2xl p-6 flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          <Loader2 size={16} className="animate-spin" />
          Loading billing analytics...
        </section>
      ) : (
        <>
          {configDraft && (
            <RoutingControlsSection
              config={configDraft}
              onChange={setConfigDraft}
              onSave={saveRuntimeConfig}
              saving={savingConfig}
            />
          )}

          <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
            <MetricCard label="Total Spend" value={formatUsdMicros(data.totals.totalCostMicros)} />
            <MetricCard label="Finalized" value={formatUsdMicros(data.totals.finalCostMicros)} />
            <MetricCard label="Estimated" value={formatUsdMicros(data.totals.estimatedCostMicros)} />
            <MetricCard label="Events" value={String(data.totals.eventCount)} />
            <MetricCard label="Avg / User" value={formatUsdMicros(data.totals.avgCostPerUserMicros)} />
          </section>

          <StackedDailyChart
            series={data.stackedDaily}
            selectedDimension={stackDimension}
            onChangeDimension={setStackDimension}
          />

          <VoiceSpendSection data={data.voice} />

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <BreakdownCard
              title="Spend by Provider"
              rows={data.topProviders}
              emptyLabel="No provider usage recorded."
            />
            <BreakdownCard
              title="Spend by Model"
              rows={data.topModels}
              emptyLabel="No model-specific usage recorded."
            />
            <BreakdownCard
              title="Spend by Service"
              rows={data.topServices}
              emptyLabel="No service usage recorded."
            />
            <BreakdownCard
              title="Spend by Channel"
              rows={data.topChannels}
              emptyLabel="No channel usage recorded."
            />
            <BreakdownCard
              title="Spend by Event Type"
              rows={data.topEventTypes}
              emptyLabel="No event types recorded."
            />
          </section>

          <DailyTrend points={data.daily} />
          <RecentEvents events={data.recentEvents} />
        </>
      )}
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="flank-card rounded-2xl p-4">
      <p className="text-xs text-[var(--color-text-secondary)]">{label}</p>
      <p className="text-lg font-semibold text-[var(--color-text-primary)] mt-1">{value}</p>
    </div>
  );
}
